import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  AuthorizedAppRootScreenParamList,
  OnboardingScreenParamList,
} from "../../../types";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { OnboardingFormValues } from "../components";
import {
  Button,
  Card,
  Page,
  Stack,
  TextField,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import {
  AccountCountryType,
  Vendor,
} from "@towersystems/roam-common/lib/generated-types";
import { Colors } from "react-native-ui-lib";
import { MUTATION_COMPLETE_ONBOARDING } from "../constants";
import { useMutation } from "@apollo/client";
import { useVendorControllerProvider } from "../../../utilities/vendor";
import React from "react";
import { useSetupAllStartupEntities } from "../../../utilities/setup";

export type OnboardingConfirmDetailsScreenProps = NativeStackScreenProps<
  OnboardingScreenParamList & AuthorizedAppRootScreenParamList,
  "OnboardingConfirmDetailsScreen"
>;

export const OnboardingConfirmDetailsScreen = ({
  navigation,
}: OnboardingConfirmDetailsScreenProps) => {
  const [hasSubmitError, setHasSubmitError] = React.useState(false);
  const { fetchAndSyncEntity } = useSetupAllStartupEntities();

  const { t } = useTranslation("onboarding");
  const { dangerouslySetCurrentVendor } = useVendorControllerProvider();
  const [m] = useMutation<{ completeOnboarding: Vendor }>(
    MUTATION_COMPLETE_ONBOARDING
  );
  const flashMessagesContext = useFlashMessages();

  const { control, reset, formState, handleSubmit, watch } =
    useFormContext<OnboardingFormValues>();

  React.useEffect(() => {
    if (hasSubmitError === true) {
      reset(undefined, {
        keepDirtyValues: true,
      });
      setHasSubmitError(false);
    }
  }, [hasSubmitError]);
  const countryType = watch("countryType");

  const abnLabel =
    countryType === AccountCountryType.AUSTRALIA ? "ABN" : "GST Account Number";

  const { isLoading, isSubmitting, isValid, isSubmitSuccessful } = formState;

  const disabled = isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const handleOnSubmit = async (data: OnboardingFormValues) => {
    try {
      const result = await m({
        variables: {
          input: {
            countryType: data.countryType,
            businessName: data.businessName,
            taxNumber: data.taxNumber,
            emailAddress: data.emailAddress,
            phoneNumber: data.phoneNumber,
            website: data.website,
          },
        },
      });
      if (result.data?.completeOnboarding) {
        dangerouslySetCurrentVendor(result.data.completeOnboarding);
        await fetchAndSyncEntity("payment_methods");
        navigation.navigate("AppRootScreen", {
          screen: "RegisterRootScreen",
        });
      }
    } catch (error) {
      console.log(error);
      setHasSubmitError(true);
      flashMessagesContext.showMessage({
        severity: "error",
        message: t("message.onboarding_error"),
      });
    }
  };

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.complete_onboarding")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      footer={footerMarkup}
      title={t("confirm_business_details_title")}
      secondaryTitle={t("confirm_business_details_secondary_text")}
    >
      <Stack vertical>
        <Controller
          control={control}
          name="businessName"
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Business name"
              value={value}
              onChangeText={onChange}
            />
          )}
        />
        <Controller
          control={control}
          name="taxNumber"
          render={({ field: { onChange, value } }) => (
            <TextField label={abnLabel} value={value} onChangeText={onChange} />
          )}
        />

        <Controller
          control={control}
          name="emailAddress"
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Email address"
              value={value}
              onChangeText={onChange}
              keyboardType="email-address"
            />
          )}
        />

        <Controller
          control={control}
          name="phoneNumber"
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Phone number"
              value={value}
              onChangeText={onChange}
              keyboardType="phone-pad"
            />
          )}
        />

        <Controller
          control={control}
          name="website"
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Website"
              value={value}
              onChangeText={onChange}
              keyboardType="url"
            />
          )}
        />
      </Stack>
    </Page>
  );
};
